import { useEffect, useState } from "react";
import { Check, Crown, Plus, CreditCard, Loader2, Link2, UserCheck, Server } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

function readMember() {
  try {
    const token = localStorage.getItem("gm_member_token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp * 1000 < Date.now()) return null;
    return payload;
  } catch {
    return null;
  }
}

const CATALOG = [
  { title: "Premium Music", items: ["Higher audio quality", "Advanced audio quality selector", "Unlimited skips", "Synced lyrics", "Offline playback"] },
  { title: "Premium Home", items: ["Advanced personalized recommendations", "Daily personalized mixes", "Weekly personalized mixes", "Advanced Made For You", "AI-powered recommendations", "Advanced listening-history recommendations"] },
  { title: "Premium Playlists", items: ["Unlimited advanced playlists", "Collaborative playlists", "Auto-generated playlists", "AI playlist cover generator", "Smart playlist sorting", "Playlist analytics", "Advanced playlist recommendations"] },
  { title: "Premium Artist Features", items: ["New-release notifications", "Artist verification badge", "Artist analytics dashboard", "Advanced artist statistics"] },
  { title: "GrooveStats Premium", items: ["Most played albums", "Weekly / monthly / yearly statistics", "Average song length", "Monthly listening reports", "Shareable statistics cards", "Advanced graphs & charts", "Advanced listening history", "Detailed listening insights"] },
  { title: "Groove AI", items: ["AI music recommendations", "AI playlist generation", "Similar-song recommendations", "Relaxing / workout / road-trip / party / gaming playlist generation", "AI artist & song discovery", "AI-generated playlist descriptions", "Natural-language music search — “Find me upbeat rap songs from the 2020s that sound similar to Travis Scott.”"] },
  { title: "Groove AI Voice Singing", beta: true, items: ["Say a few lines — GrooveMusic turns them into a sung performance using your voice", "Generate a singing version of your voice", "Adjust vocal style and effects", "Preview the generated vocals", "Beta feature — results may vary", "Only use voices you have permission/rights to use"] },
  { title: "Groove Discover Premium", items: ["Unlimited discovery", "AI-powered discovery", "Personalized discovery feed", "Genre filters", "Mood filters", "Advanced discovery preferences", "“Because you liked...” recommendations"] },
  { title: "Premium Customization", items: ["Exclusive player themes", "Animated album artwork", "Advanced visualizers", "Custom player layouts", "Custom homepage layouts", "Customizable sidebar", "Exclusive Premium effects", "Premium profile badges"] },
  { title: "Groove Wrapped Premium", items: ["Full interactive Wrapped", "Monthly listening breakdown", "Listening personality", "Top albums", "Detailed genre statistics", "Listening streak", "Most active listening times", "Year-over-year comparisons", "Animated statistics", "Advanced shareable cards", "Detailed listening insights"] },
];

export default function Premium() {
  const { site, plans } = useSite();
  const invite = site?.bot_invite_url || "#";
  const free = (plans || []).find((p) => p.key === "free");
  const pro = (plans || []).find((p) => p.key === "premium");
  const [member, setMember] = useState(readMember());
  const [guildId, setGuildId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMember(readMember());
  }, []);

  const guilds = member?.guild_names ? Object.entries(member.guild_names) : [];

  const signInDiscord = async () => {
    try {
      const res = await api.get("/auth/discord/member/url");
      window.location.href = res.data.url;
    } catch {
      toast.error("Discord sign-in is not available right now");
    }
  };

  const payStripe = async () => {
    setBusy(true);
    try {
      const headers = {};
      const mt = localStorage.getItem("gm_member_token");
      if (mt) headers.Authorization = `Bearer ${mt}`;
      const res = await api.post("/payments/checkout", {
        lookup_key: "premium_monthly",
        origin_url: window.location.origin,
        guild_id: guildId || null,
        guild_name: guildId ? member?.guild_names?.[guildId] : null,
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="premium-page">
      <section className="hero-glow border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-400 font-semibold mb-6 flex items-center justify-center gap-3">
              <Crown size={14} /> Premium
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="premium-headline">
              FREE FOREVER.<br /><span className="text-outline">PREMIUM WHEN READY.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mt-6 max-w-xl mx-auto">
              Every core feature is free. Premium just turns the dial past ten — for you, or your whole server.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-14" data-testid="premium-link-step">
        <Reveal>
          <div className={`rounded-xl border p-6 flex items-center gap-5 flex-wrap ${member ? "border-emerald-400/30 bg-emerald-400/5" : "card-beam border-blurple/40 bg-blurple/[0.06]"}`}>
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${member ? "bg-emerald-400/15 border border-emerald-400/30" : "bg-blurple/15 border border-blurple/30"}`}>
              {member ? <UserCheck size={20} className="text-emerald-400" /> : <Link2 size={20} className="text-blurple" />}
            </div>
            <div className="flex-1 min-w-[220px]">
              {member ? (
                <>
                  <h3 className="font-heading font-bold text-white">Discord linked — {member.name}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">Your premium perks unlock on the bot automatically the moment you pay.</p>
                </>
              ) : (
                <>
                  <h3 className="font-heading font-bold text-white">Step 1 — link your Discord</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">Sign in so the bot knows it's you and unlocks premium features instantly after payment.</p>
                </>
              )}
            </div>
            {!member && (
              <button onClick={signInDiscord} data-testid="premium-discord-signin"
                className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white text-sm font-heading font-bold px-6 py-3 rounded-full transition-colors duration-200">
                <Link2 size={15} /> Sign in with Discord
              </button>
            )}
          </div>
        </Reveal>
        {member && guilds.length > 0 && (
          <Reveal delay={0.1}>
            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-6 flex items-center gap-5 flex-wrap" data-testid="premium-guild-picker">
              <div className="w-11 h-11 rounded-lg bg-volt/15 border border-volt/30 flex items-center justify-center shrink-0">
                <Server size={20} className="text-volt" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <h3 className="font-heading font-bold text-white">Who gets premium?</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Your account only, or everyone in a server you manage.</p>
              </div>
              <select value={guildId} onChange={(e) => setGuildId(e.target.value)} data-testid="premium-guild-select"
                className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple [&>option]:bg-panel">
                <option value="">Just my account</option>
                {guilds.map(([id, name]) => (
                  <option key={id} value={id}>Whole server: {name}</option>
                ))}
              </select>
            </div>
          </Reveal>
        )}
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-2 gap-6" data-testid="premium-plans">
        {[free, pro].filter(Boolean).map((plan, i) => (
          <Reveal key={plan.id} delay={i * 0.1}>
            <div className={`h-full rounded-2xl p-9 flex flex-col ${
              plan.key === "premium"
                ? "card-beam border border-blurple/50 bg-blurple/[0.07] shadow-[0_0_60px_rgba(88,101,242,0.25)]"
                : "border border-white/10 bg-white/[0.03]"
            }`} data-testid={`plan-${plan.key}`}>
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl font-bold text-white">{plan.name}</h2>
                {plan.key === "premium" && (
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-blurple border border-blurple/40 rounded-full px-3 py-1">Most popular</span>
                )}
              </div>
              <p className="mt-5">
                <span className="font-display text-4xl text-white" style={{ fontWeight: 800 }}>
                  ${plan.price.toFixed(2)}
                </span>
                <span className="text-sm text-white/40 font-mono"> /month</span>
              </p>
              <ul className="mt-8 space-y-3.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                    <Check size={16} className={plan.key === "premium" ? "text-blurple mt-0.5 shrink-0" : "text-emerald-400 mt-0.5 shrink-0"} />
                    {f}
                  </li>
                ))}
              </ul>
              {plan.key === "premium" ? (
                <div className="mt-9 space-y-3">
                  <button
                    onClick={member ? payStripe : signInDiscord}
                    disabled={busy}
                    data-testid="upgrade-stripe-button"
                    className="w-full inline-flex items-center justify-center gap-2 bg-blurple hover:bg-[#4752c4] disabled:opacity-60 text-white font-heading font-bold py-3.5 rounded-full transition-colors duration-200"
                  >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : member ? <CreditCard size={16} /> : <Link2 size={16} />}
                    {member ? "Step 2 — Upgrade with card" : "Link Discord to upgrade"}
                  </button>
                  {!member && (
                    <p className="text-center text-[11px] text-amber-300/80">Premium payment needs a linked Discord so the bot unlocks your perks instantly. The free plan needs nothing.</p>
                  )}
                  <p className="text-center text-[11px] font-mono text-white/30">Secure checkout · discount codes accepted · cancel anytime · powered by Stripe</p>
                </div>
              ) : (
                <a
                  href={invite}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="plan-cta-free"
                  className="mt-9 inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 text-white font-heading font-bold py-3.5 rounded-full transition-colors duration-200"
                >
                  <Plus size={16} strokeWidth={3} /> Add for free
                </a>
              )}
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20" data-testid="premium-catalog">
        <Reveal>
          <div className="flex items-center gap-4 mb-8">
            <span className="w-8 h-px bg-blurple" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">The full Premium deck</h2>
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400 border border-amber-400/30 rounded-full px-3 py-1">Beta features roll out progressively</span>
          </div>
        </Reveal>
        <Accordion type="multiple" className="grid md:grid-cols-2 gap-3">
          {CATALOG.map((cat, i) => (
            <Reveal key={cat.title} delay={(i % 2) * 0.06}>
              <AccordionItem value={`cat-${i}`} className="border border-white/10 rounded-xl bg-white/[0.03] px-6 data-[state=open]:border-blurple/40" data-testid={`catalog-${cat.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <AccordionTrigger className="text-left font-heading font-semibold text-white hover:no-underline py-5 text-base">
                  <span className="flex items-center gap-2.5 flex-wrap">
                    {cat.title}
                    {cat.beta && <span className="text-[10px] font-mono uppercase tracking-wider text-volt border border-volt/30 rounded-full px-2 py-0.5">Beta</span>}
                    <span className="text-[10px] font-mono text-white/30">{cat.items.length}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <ul className="space-y-2">
                    {cat.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <Check size={14} className="text-blurple mt-1 shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Reveal>
          ))}
        </Accordion>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-24 text-center">
        <Reveal>
          <p className="text-sm text-white/40 leading-relaxed">
            Premium is managed right inside Discord with <code className="font-mono text-volt">!premium</code>. Cancel anytime — your server keeps its playlists.
          </p>
        </Reveal>
      </section>
    </main>
  );
}
